import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

// Accessible modal dialog rendered into document.body via a portal.
// - role="dialog" + aria-modal, labelled by the title when provided
// - Escape and backdrop click close it
// - moves focus into the dialog on open
export default function Modal({ isOpen, onClose, title, children, dismissOnBackdropClick = true }) {
  const dialogRef = useRef(null);

  // Move focus into the dialog ONCE when it opens. Depending on `onClose` here
  // would re-run on every parent render (onClose is a fresh closure each time)
  // and steal focus back from inputs after each keystroke.
  useEffect(() => {
    if (isOpen) dialogRef.current?.focus();
  }, [isOpen]);

  // Escape-to-close listener, kept separate so it can track the latest onClose
  // without re-triggering the focus effect above.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const titleId = 'ss-modal-title';

  return createPortal(
    <div
      className="ss-modal-overlay"
      onMouseDown={(e) => {
        if (dismissOnBackdropClick && e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="ss-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        ref={dialogRef}
      >
        <button className="ss-modal__close" aria-label="Close" onClick={onClose}>
          <X size={20} aria-hidden="true" />
        </button>
        {title && (
          <h2 id={titleId} style={{ marginBottom: 'var(--ss-space-4)', paddingRight: 'var(--ss-space-6)' }}>
            {title}
          </h2>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
}
